<script setup lang="ts">
import { asText } from '@prismicio/client'
import { components } from '~/slices'

const { client } = usePrismic()
const route = useRoute()
const { data: vacancy } = await useAsyncData(
  `vacancy-${route.params.uid}`,
  () => client.getByUID('vacancy', route.params.uid as string),
)

useDocumentSeo(vacancy)
useJobPostingSchema(vacancy)

// Sidebar spec rows — richer display fields fall back to the Select values.
const spec = computed(() => {
  const d = vacancy.value?.data as Record<string, any> | undefined
  if (!d) return []
  return [
    { key: 'Dienstverband', val: d.employment_type },
    { key: 'Uren per week', val: d.hours_per_week },
    { key: 'Werkgebied', val: d.work_area || d.location },
    { key: 'Niveau', val: d.education_level || d.level },
    { key: 'Salaris', val: d.salary_range },
    { key: 'Vakgebied', val: d.discipline },
  ].filter((r) => r.val)
})

const contactFirstName = computed(
  () => vacancy.value?.data.contact_name?.split(' ')[0] || '',
)

// Hero badges: "Fulltime · 32–40 uur", "Ede en omstreken", "Ervaren" (accent).
const heroBadges = computed(() => {
  const d = vacancy.value?.data as Record<string, any> | undefined
  if (!d) return []
  const out: { label: string; accent?: boolean }[] = []
  const type = [d.employment_type, d.hours_per_week].filter(Boolean).join(' · ')
  if (type) out.push({ label: type })
  const area = d.work_area || d.location
  if (area) out.push({ label: area })
  if (d.level) out.push({ label: d.level, accent: true })
  return out
})
</script>

<template>
  <main v-if="vacancy">
    <VacancyHero
      :title="asText(vacancy.data.title)"
      :image="vacancy.data.featured_image"
      :badges="heroBadges"
    />

    <div class="pa-vacancy-detail">
      <div class="pa-container">
      <div class="pa-vacancy-detail__layout">
        <!-- Main column -->
        <div class="pa-vacancy-detail__main">
          <section class="pa-vacancy-detail__section">
            <h2 class="pa-vacancy-detail__section-title">Over de functie</h2>
            <div class="pa-vacancy-detail__prose">
              <PrismicRichText :field="vacancy.data.introduction" />
            </div>
          </section>

          <section
            v-if="vacancy.data.what_you_will_do?.length"
            class="pa-vacancy-detail__section"
          >
            <h2 class="pa-vacancy-detail__section-title">Wat ga je doen?</h2>
            <ol class="pa-vacancy-detail__tasks">
              <li
                v-for="(row, i) in vacancy.data.what_you_will_do"
                :key="i"
                class="pa-vacancy-detail__task"
              >
                <span class="pa-vacancy-detail__task-num">
                  {{ String(i + 1).padStart(2, '0') }}
                </span>
                <span>{{ row.item }}</span>
              </li>
            </ol>
          </section>

          <div class="pa-vacancy-detail__panels">
            <div
              v-if="vacancy.data.what_we_search?.length"
              class="pa-vacancy-detail__panel pa-vacancy-detail__panel--ask"
            >
              <h2 class="pa-vacancy-detail__panel-title">Wat vragen wij</h2>
              <ul class="pa-vacancy-detail__checklist">
                <li
                  v-for="(row, i) in vacancy.data.what_we_search"
                  :key="i"
                  class="pa-vacancy-detail__check"
                >
                  <svg
                    class="pa-vacancy-detail__check-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="12" fill="currentColor" />
                    <path
                      d="M7 12.5l3 3 7-7"
                      stroke="#fff"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span>{{ row.item }}</span>
                </li>
              </ul>
            </div>

            <div
              v-if="vacancy.data.employment_conditions?.length"
              class="pa-vacancy-detail__panel pa-vacancy-detail__panel--offer"
            >
              <h2 class="pa-vacancy-detail__panel-title">Wat bieden wij</h2>
              <ul class="pa-vacancy-detail__checklist">
                <li
                  v-for="(row, i) in vacancy.data.employment_conditions"
                  :key="i"
                  class="pa-vacancy-detail__check"
                >
                  <svg
                    class="pa-vacancy-detail__check-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="12" fill="currentColor" />
                    <path
                      d="M7 12.5l3 3 7-7"
                      stroke="#fff"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span>{{ row.item }}</span>
                </li>
              </ul>
            </div>
          </div>

          <section
            v-if="vacancy.data.extra_text?.length || vacancy.data.extra_text_title"
            class="pa-vacancy-detail__section"
          >
            <h2
              v-if="vacancy.data.extra_text_title"
              class="pa-vacancy-detail__section-title"
            >
              {{ vacancy.data.extra_text_title }}
            </h2>
            <div class="pa-vacancy-detail__prose">
              <PrismicRichText :field="vacancy.data.extra_text" />
            </div>
          </section>

          <VacancyApplicationForm
            :vacancy-uid="vacancy.uid"
            :vacancy-title="asText(vacancy.data.title)"
            :contact-email="vacancy.data.contact_email || ''"
          />
        </div>

        <!-- Sidebar -->
        <aside class="pa-vacancy-detail__aside">
          <div class="pa-vacancy-detail__spec">
            <div class="pa-vacancy-detail__spec-label">Vacature</div>
            <p class="pa-vacancy-detail__spec-title">{{ asText(vacancy.data.title) }}</p>

            <dl class="pa-vacancy-detail__spec-rows">
              <div
                v-for="row in spec"
                :key="row.key"
                class="pa-vacancy-detail__spec-row"
              >
                <dt class="pa-vacancy-detail__spec-key">{{ row.key }}</dt>
                <dd class="pa-vacancy-detail__spec-val">{{ row.val }}</dd>
              </div>
            </dl>

            <a href="#vacancy-apply" class="pa-vacancy-detail__spec-cta">
              Solliciteer direct &rarr;
            </a>
            <p
              v-if="contactFirstName && vacancy.data.contact_phone"
              class="pa-vacancy-detail__spec-phone"
            >
              of bel {{ contactFirstName }} op {{ vacancy.data.contact_phone }}
            </p>
          </div>

          <div v-if="vacancy.data.contact_name" class="pa-vacancy-detail__contact">
            <PrismicImage
              v-if="vacancy.data.contact_photo?.url"
              :field="vacancy.data.contact_photo"
              class="pa-vacancy-detail__contact-photo"
              :widths="[56, 112]"
            />
            <div>
              <span class="pa-vacancy-detail__contact-note">Vragen? Bel of mail</span>
              <span class="pa-vacancy-detail__contact-name">{{ vacancy.data.contact_name }}</span>
              <a
                v-if="vacancy.data.contact_email"
                :href="`mailto:${vacancy.data.contact_email}`"
                class="pa-vacancy-detail__contact-email"
              >{{ vacancy.data.contact_email }}</a>
            </div>
          </div>
        </aside>
      </div>
      </div>
    </div>

    <SliceZone :slices="vacancy.data.slices ?? []" :components="components" />
  </main>
</template>
